import { useState } from 'react'
import { Check, Receipt, Search, TriangleAlert, X } from 'lucide-react'
import type { Kijk as KijkBlok } from '../lib/types'

/* ===========================================================================
 *  De factuur nalopen
 *
 *  Een nagebouwde kostenpost, precies zoals hij op het scherm staat, met
 *  twee dingen erin die niet kloppen. Tik aan wat je niet vertrouwt.
 *
 *  Waarom dit geen lijstje met aandachtspunten is
 *  ----------------------------------------------
 *
 *  Je kunt opschrijven waar iemand naar moet kijken. Dat leest hij, hij knikt,
 *  en morgen klikt hij alsnog op goedkeuren omdat het er goed uitzag. Wat wél
 *  blijft hangen is één keer zelf de regel aanwijzen waar het misgaat -- en
 *  daarna is het die regel die je de volgende keer als eerste leest.
 *
 *  Waarom de kaart lijkt op het echte scherm
 *  -----------------------------------------
 *
 *  Omdat herkenning de helft van het werk is. Een geschematiseerde oefening
 *  leert je de oefening; deze leert je het scherm. Dezelfde volgorde,
 *  dezelfde woorden, dezelfde merkjes achter de waarden.
 *
 *  Er staat geen strafpunt op het aanwijzen van een regel die wél klopt. Wie
 *  te voorzichtig wordt, kijkt niet meer -- en dat is niet het gedrag dat we
 *  willen aanleren.
 * =========================================================================== */

export default function Kijk({
  blok, opKlaar,
}: {
  blok: KijkBlok
  opKlaar: () => void
}) {
  const [aangewezen, setAangewezen] = useState<Set<string>>(new Set())
  const [nagekeken, setNagekeken] = useState(false)

  function tik(id: string) {
    if (nagekeken) return
    setAangewezen((was) => {
      const nu = new Set(was)
      if (nu.has(id)) nu.delete(id); else nu.add(id)
      return nu
    })
  }

  function nakijken() {
    setNagekeken(true)
    opKlaar()
  }

  const fouten = blok.regels.filter((r) => r.mis)
  const gevonden = fouten.filter((r) => aangewezen.has(r.id)).length
  const teveel = blok.regels.filter((r) => !r.mis && aangewezen.has(r.id)).length

  return (
    <div className="kijk">
      <p className="inleiding">{blok.inleiding}</p>

      <div className="bonkaart">
        <div className="bonkop">
          <Receipt size={16} />
          <span>Kostenpost</span>
          <span className="rek" />
          <span className="stand">Te valideren</span>
        </div>

        <ul className="bonregels">
          {blok.regels.map((regel, i) => {
            const gekozen = aangewezen.has(regel.id)
            let klasse = 'bonregel'
            if (nagekeken) {
              if (gekozen && regel.mis) klasse += ' raak'
              else if (gekozen && !regel.mis) klasse += ' loos'
              else if (!gekozen && regel.mis) klasse += ' gemist'
            } else if (gekozen) {
              klasse += ' gekozen'
            }

            return (
              <li key={regel.id}>
                <button
                  type="button"
                  className={klasse}
                  onClick={() => tik(regel.id)}
                  disabled={nagekeken}
                  aria-pressed={gekozen}
                >
                  <span className="nr">{i + 1}</span>
                  <span className="label">{regel.label}</span>
                  <span className="waarde">
                    {regel.waarde}
                    {regel.merk && (
                      <span className={`merkje ${regel.merk.soort}`}>{regel.merk.tekst}</span>
                    )}
                  </span>
                </button>

                {nagekeken && (
                  <div className={`nadien ${regel.mis ? 'mis' : 'goed'}`}>
                    {regel.mis ? <X size={15} /> : <Check size={15} />}
                    <span>
                      <strong>{regel.mis ? 'Hier zit het fout. ' : 'Waar je hier naar kijkt: '}</strong>
                      {regel.mis ?? regel.controle}
                    </span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </div>

      {!nagekeken ? (
        <button
          className="knop hoofd breed"
          onClick={nakijken}
          disabled={aangewezen.size === 0}
        >
          <Search size={17} />
          {aangewezen.size === 0
            ? 'Tik eerst aan wat je niet vertrouwt'
            : `Nakijken (${aangewezen.size} aangetikt)`}
        </button>
      ) : (
        <div className="opkomst" style={{ display: 'grid', gap: 12 }}>
          <div className={`uitslag ${gevonden === fouten.length ? 'goed' : 'mis'}`}>
            {gevonden === fouten.length ? <Check size={18} /> : <TriangleAlert size={18} />}
            <span>
              <span className="titel">
                Je vond {gevonden} van de {fouten.length}
                {teveel > 0 && ` (en tikte ${teveel} regel${teveel > 1 ? 's' : ''} aan die klopten)`}.
              </span>
              {blok.naderhand}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
